import { useCallback, useState } from "react";

import { PhoneCall, PhoneIncoming, PhoneMissed, Timer, Smartphone, Loader2 } from "lucide-react";

import { Button } from "../../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import MainContainer from "../../components/MainContainer";
import usePermissions from "../../hooks/usePermissions";
import { useLigacoes, abrirTelefone } from "../../ligacoes/store";
import Historico, { mmss } from "./Historico";
import Aparelhos from "./Aparelhos";
import Configuracoes from "./Configuracoes";
import EsteComputador from "./EsteComputador";

// Página do módulo de ligações: o que aconteceu (histórico e gravações), os
// aparelhos de cada número, as regras da empresa e o microfone desta máquina.
// Só aparece no menu com o módulo instalado, rodando e contratado.

const Cartao = ({ icon: Icon, rotulo, valor, cor }) => (
	<div className="rounded-xl border bg-card p-3">
		<div className="flex items-center gap-2 text-xs text-muted-foreground">
			<Icon className={`h-4 w-4 ${cor || ""}`} />
			{rotulo}
		</div>
		<p className="mt-1 text-2xl font-semibold tabular-nums">{valor}</p>
	</div>
);

const Ligacoes = () => {
	const lig = useLigacoes();
	const { temPermissao, carregando } = usePermissions();
	const [aba, setAba] = useState("historico");
	const [resumo, setResumo] = useState(null);

	const aoResumo = useCallback((d) => setResumo(d), []);

	const gerencia = temPermissao("connections:manage");
	const t = resumo?.totals || {};
	const total = Object.values(t).reduce((a, b) => a + b, 0);

	if (carregando) {
		return (
			<MainContainer>
				<div className="flex flex-1 items-center justify-center">
					<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
				</div>
			</MainContainer>
		);
	}

	return (
		<MainContainer>
			<div className="flex flex-wrap items-center gap-2 border-b px-4 py-3 sm:px-6">
				<div className="min-w-0 flex-1">
					<h1 className="truncate text-lg font-semibold tracking-tight">Ligações</h1>
					<p className="text-xs text-muted-foreground">
						Ligações de WhatsApp feitas e recebidas pelo navegador
					</p>
				</div>
				{temPermissao("calls:make") && (
					<Button
						className="h-9 bg-emerald-600 text-white hover:bg-emerald-700"
						disabled={!lig.disponivel}
						title={lig.disponivel ? "Abrir o teclado" : "Pareie um aparelho de ligações primeiro"}
						onClick={() => abrirTelefone()}
					>
						<PhoneCall className="h-4 w-4" />
						Nova ligação
					</Button>
				)}
			</div>

			<div className="flex-1 overflow-auto p-4 sm:p-6">
				{!lig.disponivel && gerencia && aba !== "aparelhos" && (
					<div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
						<Smartphone className="h-5 w-5 text-amber-600" />
						<span className="flex-1">Nenhum número tem aparelho de ligações conectado ainda.</span>
						<Button size="sm" onClick={() => setAba("aparelhos")}>
							Parear agora
						</Button>
					</div>
				)}

				<div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
					<Cartao icon={PhoneCall} rotulo="Ligações hoje" valor={resumo ? total : "—"} />
					<Cartao icon={PhoneIncoming} rotulo="Atendidas hoje" valor={resumo ? t.answered || 0 : "—"} cor="text-emerald-600" />
					<Cartao icon={PhoneMissed} rotulo="Perdidas hoje" valor={resumo ? t.missed || 0 : "—"} cor="text-destructive" />
					<Cartao
						icon={Timer}
						rotulo="Duração média hoje"
						valor={resumo ? mmss(resumo.avgDuration || 0) : "—"}
					/>
				</div>

				<Tabs value={aba} onValueChange={setAba}>
					<TabsList className="mb-3 flex-wrap">
						<TabsTrigger value="historico">Histórico</TabsTrigger>
						{gerencia && <TabsTrigger value="aparelhos">Aparelhos</TabsTrigger>}
						<TabsTrigger value="configuracoes">Configurações</TabsTrigger>
						<TabsTrigger value="computador">Este computador</TabsTrigger>
					</TabsList>
					<TabsContent value="historico">
						<Historico aoResumo={aoResumo} />
					</TabsContent>
					{gerencia && (
						<TabsContent value="aparelhos">
							<Aparelhos />
						</TabsContent>
					)}
					<TabsContent value="configuracoes">
						<Configuracoes />
					</TabsContent>
					<TabsContent value="computador">
						<EsteComputador />
					</TabsContent>
				</Tabs>
			</div>
		</MainContainer>
	);
};

export default Ligacoes;
